"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion, type Variants } from "motion/react";
import { usePathname } from "next/navigation";
import { Grid3X3, X } from "lucide-react";
import { cn } from "@/util/cn";
import { addToast } from "@heroui/react";
import { useConnection } from "wagmi";
import { usePortfolioOpen } from "@/hooks/modals/usePortfolioOpen";
import { navCategories, springTransition, type NavCategory } from "./mobile-nav-config";
import { MobileNavDrawer } from "./mobile-nav-drawer";
import { MobileNavSubnav } from "./mobile-nav-subnav";
export default function MobileBottomNav() {
  const pathname = usePathname();
  const {
    isConnected
  } = useConnection();
  const {
    onOpen: onPortfolioOpen
  } = usePortfolioOpen();
  const prefersReducedMotion = useReducedMotion();

  // Navigation state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<NavCategory | null>(null);

  // Refs for focus management
  const drawerTriggerRef = useRef<HTMLButtonElement>(null);
  const touchStartRef = useRef<{
    x: number;
    y: number;
    time: number;
  } | null>(null);

  // Swipe gesture handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  }, []);
  const handleTouchEnd = useCallback((e_0: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch_0 = e_0.changedTouches[0];
    const deltaX = touch_0.clientX - touchStartRef.current.x;
    const deltaY = touch_0.clientY - touchStartRef.current.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - touchStartRef.current.time;
    const navWidth = (e_0.currentTarget as HTMLElement).offsetWidth;
    const startX = touchStartRef.current.x;

    // Swipe up to open drawer (from anywhere on the nav)
    if (deltaY < -60 && absDeltaX < 50 && deltaTime < 500) {
      isDrawerOpen ? handleDrawerToggle() : setIsDrawerOpen(true);
      touchStartRef.current = null;
      return;
    }

    // Swipe right to go back (when in submenu)
    if (deltaX > 80 && absDeltaY < 50 && deltaTime < 500 && activeCategory) {
      setActiveCategory(null);
      touchStartRef.current = null;
      return;
    }

    // Swipe left on right side to open portfolio
    const isRightSideSwipe = startX >= navWidth / 2;
    const isValidLeftSwipe = deltaX < -100 && absDeltaY < 50 && deltaTime < 1000;
    if (isRightSideSwipe && isValidLeftSwipe && !activeCategory) {
      if (!isConnected) {
        addToast({
          title: "Wallet Required",
          description: "Please connect your wallet to view your portfolio",
          color: "primary",
          timeout: 3000
        });
      } else {
        onPortfolioOpen();
      }
    }
    touchStartRef.current = null;
  }, [isConnected, onPortfolioOpen, activeCategory, isDrawerOpen]);

  // Keyboard navigation for escape key
  useEffect(() => {
    const handleKeyDown = (e_1: KeyboardEvent) => {
      if (e_1.key === "Escape") {
        if (isDrawerOpen) {
          setIsDrawerOpen(false);
          drawerTriggerRef.current?.focus();
        } else if (activeCategory) {
          setActiveCategory(null);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDrawerOpen, activeCategory]);
  const handleCategoryClick = (category: NavCategory) => {
    // Close drawer if open
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
    // Toggle or switch category
    if (activeCategory?.id === category.id) {
      setActiveCategory(null);
    } else {
      setActiveCategory(category);
    }
  };
  const handleDrawerToggle = () => {
    // Close sub-nav if open
    if (activeCategory) {
      setActiveCategory(null);
    }
    setIsDrawerOpen(!isDrawerOpen);
  };
  const handleBack = () => {
    setActiveCategory(null);
  };
  const isCategoryActive = (category_0: NavCategory) => {
    return category_0.subItems.some(item => {
      if (item.href === "/") return pathname === "/";
      return pathname === item.href || pathname.startsWith(item.href + "/");
    });
  };

  // Animation variants for root nav items
  const rootNavVariants: Variants = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      x: -15,
      opacity: 0
    },
    animate: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30,
        staggerChildren: 0.025
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.025
      }
    }
  };
  const itemVariants: Variants = prefersReducedMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      x: -12,
      opacity: 0
    },
    animate: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.01
      }
    }
  };
  const drawerVariants: Variants = prefersReducedMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      scale: 0.25,
      opacity: 0,
      y: 69
    },
    animate: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        visualDuration: 0.05,
        bounce: 0.25
      }
    },
    exit: {
      scale: 0.25,
      opacity: 0,
      y: 69
    }
  };

  // Get categories split for left and right of center button
  const leftCategories = navCategories.slice(0, 2);
  const rightCategories = navCategories.slice(2);
  return <>
      {/* App Drawer */}
      <MobileNavDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} triggerRef={drawerTriggerRef} />

      {/* Bottom Navigation Bar */}
      <nav role="navigation" aria-label="Main navigation" className="fixed bottom-0 left-0 right-0 z-50 md:hidden touch-none" style={{
      paddingBottom: "env(safe-area-inset-bottom)"
    }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {/* Screen reader announcement */}
        <span className="sr-only" aria-live="polite">
          {activeCategory ? `${activeCategory.label} submenu, ${activeCategory.subItems.length} options` : "Main navigation"}
        </span>

        {/* Glass morphism background */}
        <div className="absolute inset-0 bg-background/80 backdrop-blur-xl border-t border-white/10" />

        {/* Navigation content */}
        <div className="relative flex items-center h-[72px] px-2">
          <AnimatePresence mode="popLayout">
            {activeCategory ? (/* Sub-navigation mode */
          <MobileNavSubnav category={activeCategory} onBack={handleBack} />) : (/* Root navigation mode */
          <motion.div key="root-nav" className="flex items-center justify-between w-full" variants={rootNavVariants} initial="initial" animate="animate" exit="exit">
                {/* Left categories */}
                <div className="flex items-center justify-around flex-1">
                  {leftCategories.map(category_1 => <NavCategoryButton key={category_1.id} category={category_1} isActive={isCategoryActive(category_1)} isExpanded={false} onClick={() => handleCategoryClick(category_1)} variants={itemVariants} pathname={pathname} />)}
                </div>

                {/* Center drawer button */}
                <motion.button ref={drawerTriggerRef} variants={drawerVariants} whileTap={{
              scale: 0.9
            }} onClick={handleDrawerToggle} aria-expanded={isDrawerOpen} aria-haspopup="dialog" aria-controls="app-drawer" aria-label={isDrawerOpen ? "Close navigation menu" : "Open navigation menu"} className={cn("relative -mt-6 z-10", "w-14 h-14 rounded-2xl", "bg-secondary shadow-lg shadow-secondary/30", "flex items-center justify-center", "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-background", "transition-transform")}>
                  <motion.div animate={{
                rotate: isDrawerOpen ? 45 : 0
              }} transition={springTransition}>
                    {isDrawerOpen ? <X className="h-6 w-6 text-white rotate-45" aria-hidden="true" /> : <Grid3X3 className="h-6 w-6 text-white" aria-hidden="true" />}
                  </motion.div>
                </motion.button>

                {/* Right categories */}
                <div className="flex items-center justify-around flex-1">
                  {rightCategories.map(category_2 => <NavCategoryButton key={category_2.id} category={category_2} isActive={isCategoryActive(category_2)} isExpanded={false} onClick={() => handleCategoryClick(category_2)} variants={itemVariants} pathname={pathname} />)}
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </nav>
    </>;
}

// Separate component for nav category buttons
interface NavCategoryButtonProps {
  category: NavCategory;
  isActive: boolean;
  isExpanded: boolean;
  onClick: () => void;
  variants: Variants;
  pathname: string;
}
function NavCategoryButton(t0) {
  const $ = _c(36);
  const {
    category,
    isActive,
    isExpanded,
    onClick,
    variants,
    pathname
  } = t0;
  let t1;
  if ($[0] !== category.subItems || $[1] !== pathname) {
    let t2;
    if ($[3] !== pathname) {
      t2 = item => {
        if (!item.href) {
          return false;
        }
        if (item.href === "/") {
          return pathname === "/";
        }
        return pathname === item.href || pathname.startsWith(`${item.href}/`);
      };
      $[3] = pathname;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = category.subItems.find(t2);
    $[0] = category.subItems;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeSubItem = t1;
  const Icon = activeSubItem?.icon ?? category.icon;
  const label = activeSubItem?.label ?? category.label;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      scale: 0.9
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `subnav-${category.id}`;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("flex flex-col items-center justify-center py-1 min-h-[44px] min-w-[44px] px-2", "focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-xl");
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isActive) {
    t5 = isActive && <motion.div layoutId="activeTab" className="absolute -top-1 w-10 h-1 bg-secondary rounded-full" transition={{
      type: "spring",
      stiffness: 500,
      damping: 30
    }} />;
    $[7] = isActive;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isActive) {
    t6 = isActive && <motion.div className="absolute inset-0 bg-secondary/20 rounded-full blur-md" initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1.2,
      opacity: 1
    }} transition={{
      ease: "anticipate",
      duration: 0.5,
      delay: 0.05
    }} aria-hidden="true" />;
    $[9] = isActive;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = isActive ? "bg-secondary/10 text-secondary" : "text-default-600 hover:text-white";
  let t8;
  if ($[11] !== t7) {
    t8 = cn("p-2 rounded-2xl transition-colors relative", t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== Icon) {
    t9 = <Icon className="h-6 w-6" aria-hidden="true" />;
    $[13] = Icon;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <div className="relative">{t6}{t10}</div>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = isActive ? "text-secondary" : "text-default-600";
  let t13;
  if ($[21] !== t12) {
    t13 = cn("text-xs font-medium mt-1 transition-colors", t12);
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== label || $[24] !== t13) {
    t14 = <span className={t13}>{label}</span>;
    $[23] = label;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t11 || $[27] !== t14 || $[28] !== t5) {
    t15 = <div className="relative flex flex-col items-center">{t5}{t11}{t14}</div>;
    $[26] = t11;
    $[27] = t14;
    $[28] = t5;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== isExpanded || $[31] !== onClick || $[32] !== t15 || $[33] !== t3 || $[34] !== variants) {
    t16 = <motion.button variants={variants} whileTap={t2} onClick={onClick} aria-expanded={isExpanded} aria-haspopup="true" aria-controls={t3} className={t4}>{t15}</motion.button>;
    $[30] = isExpanded;
    $[31] = onClick;
    $[32] = t15;
    $[33] = t3;
    $[34] = variants;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}